import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { lumi } from '@/lib/lumi'

const TemplatesPage = () => {
  const [downloading, setDownloading] = useState<string | null>(null)

  const handleDownloadBlankTemplate = async (format: 'csv' | 'html' | 'txt') => {
    setDownloading(format)
    try {
      const result = await lumi.functions.invoke('generateTemplate', {
        method: 'POST',
        body: {
          format,
          businessName: '[Your Business Name]',
          clientName: '[Client Name]',
          invoiceNumber: '[INV-001]',
          invoiceDate: new Date().toISOString().split('T')[0],
          items: [{ description: '[Service/Product]', quantity: 1, rate: 0 }],
          totalAmount: 0,
          currency: 'USD',
          filled: false
        }
      })

      if (result.success) {
        const blob = new Blob([result.content], { type: result.contentType })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = result.filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }
    } catch (error: any) {
      alert('Failed to download template. Please try again.')
    } finally {
      setDownloading(null)
    }
  }

  const templates = [
    {
      format: 'Word',
      icon: '📄',
      description: 'Editable invoice format in Word (DOCX) for Microsoft Office and Google Docs.',
      features: ['Fully customizable', 'Works offline', 'Professional formatting'],
      color: 'from-blue-50 to-blue-100'
    },
    {
      format: 'Excel',
      icon: '📊',
      description: 'Invoice format in Excel (XLSX) with automatic calculations and formulas.',
      features: ['Auto-calculate totals', 'Track payments', 'Export to PDF'],
      color: 'from-green-50 to-green-100'
    },
    {
      format: 'PDF',
      icon: '📑',
      description: 'Fillable PDF invoice templates that work on any device.',
      features: ['Universal compatibility', 'Print-ready', 'Secure & professional'],
      color: 'from-emerald-50 to-emerald-100'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 to-emerald-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Download Invoice Templates (Word, Excel, PDF)
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Free professional invoice templates in multiple formats. Choose the invoice format that works best for your business.
          </p>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {templates.map((template) => (
            <div
              key={template.format}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className={`bg-gradient-to-br ${template.color} p-8 text-center`}>
                <div className="text-6xl mb-4">{template.icon}</div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {template.format} Invoice Template
                </h2>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-6">{template.description}</p>
                <ul className="space-y-3 mb-6">
                  {template.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button 
                  onClick={() => handleDownloadBlankTemplate(
                    template.format === 'Word' ? 'html' : 
                    template.format === 'Excel' ? 'csv' : 'txt'
                  )}
                  disabled={downloading === (template.format === 'Word' ? 'html' : template.format === 'Excel' ? 'csv' : 'txt')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloading === (template.format === 'Word' ? 'html' : template.format === 'Excel' ? 'csv' : 'txt') 
                    ? 'Downloading...' 
                    : `Download ${template.format} Template`}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Conversion CTA */}
        <div className="bg-gradient-to-r from-emerald-600 to-blue-600 rounded-3xl shadow-2xl p-12 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Tired of Manual Word Docs?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Skip the hassle of editing templates. Our automatic invoice generator creates professional invoices in seconds—no downloads required.
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-blue-600 hover:bg-blue-50 font-bold text-lg py-4 px-10 rounded-full transition-all duration-200 hover:scale-105 shadow-lg"
          >
            Switch to Our Automatic Generator →
          </Link>
        </div>

        {/* SEO Content Section */}
        <div className="mt-16 grid md:grid-cols-2 gap-12">
          <div className="bg-white rounded-xl shadow-md p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Why Use Invoice Templates?
            </h3>
            <p className="text-gray-600 mb-4">
              Invoice templates provide a quick starting point for freelancers and small businesses. Whether you need an invoice format in Word for easy editing, an Excel invoice with automatic calculations, or a fillable PDF invoice for universal compatibility, we've got you covered.
            </p>
            <p className="text-gray-600">
              Our templates include all essential fields: client details, itemized services, tax calculations, payment terms, and professional branding space.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              What's Included?
            </h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="text-emerald-600 font-bold mr-2">•</span>
                <span><strong>Simple invoice format</strong> that's easy to customize</span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 font-bold mr-2">•</span>
                <span><strong>Professional layout</strong> for all business types</span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 font-bold mr-2">•</span>
                <span><strong>Tax invoice compliance</strong> ready formatting</span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 font-bold mr-2">•</span>
                <span><strong>Multi-currency support</strong> for international clients</span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 font-bold mr-2">•</span>
                <span><strong>Print and email ready</strong> formats</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Format Comparison Table */}
        <div className="mt-16 bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-emerald-600 px-8 py-6">
            <h3 className="text-2xl font-bold text-white">
              Compare Invoice Formats
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Feature</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Word</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Excel</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-700">Easy Editing</td>
                  <td className="px-6 py-4 text-center text-green-600">✓</td>
                  <td className="px-6 py-4 text-center text-green-600">✓</td>
                  <td className="px-6 py-4 text-center text-gray-400">—</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-700">Auto Calculations</td>
                  <td className="px-6 py-4 text-center text-gray-400">—</td>
                  <td className="px-6 py-4 text-center text-green-600">✓</td>
                  <td className="px-6 py-4 text-center text-gray-400">—</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-700">Universal Compatibility</td>
                  <td className="px-6 py-4 text-center text-gray-400">—</td>
                  <td className="px-6 py-4 text-center text-gray-400">—</td>
                  <td className="px-6 py-4 text-center text-green-600">✓</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-700">Print Ready</td>
                  <td className="px-6 py-4 text-center text-green-600">✓</td>
                  <td className="px-6 py-4 text-center text-green-600">✓</td>
                  <td className="px-6 py-4 text-center text-green-600">✓</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TemplatesPage
